import { useMemo } from 'react'
import { useFilterState } from '@editor/pages/StructuralPlanning/hooks/useFilterState'
import { useModelStore, useControlStore } from '@editor/stores'
import useHiddenElements from './useHiddenElements'
import useIsPlanView from './useIsPlanView'

const usePositionVisibilityLookup = () => {
  const { rips, lintels } = useModelStore(state => state.model)
  const { showRips, showLintels, positionVisibilityFollowsWallVisibility } = useFilterState()
  const hiddenElements = useHiddenElements()
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const isPlanView = useIsPlanView()

  const positions = useMemo(() => [...(lintels || []), ...(rips || [])], [lintels, rips])

  const positionVisibilityLookup = useMemo(() => {
    return positions.reduce((collector, position) => {
      const isPositionHidden = hiddenElements.has(position.position_guid)
      const isElementTypeVisible =
        (showRips && position.element_type === 'rips') ||
        (showLintels && position.element_type === 'lintels')
      const isWallVisible =
        // wall could be storey wise disabled
        !hiddenElements.has(position.wall_guid) &&
        // wall could be individually hidden
        !hiddenElementIds.has(position.wall_guid)
      const isVisible =
        // hidden by type, explicitly or by storey (in the Planansicht: by its plan storeys, as
        // the exported plan shows it, whichever storey its wall is on)
        !isPositionHidden &&
        isElementTypeVisible &&
        // position could be individually hidden
        !hiddenElementIds.has(position.position_guid) &&
        (positionVisibilityFollowsWallVisibility && !isPlanView ? isWallVisible : true)
      return { ...collector, [position.position_guid]: isVisible }
    }, {} as Record<string, boolean>)
  }, [
    positions,
    hiddenElements,
    hiddenElementIds,
    isPlanView,
    showRips,
    showLintels,
    positionVisibilityFollowsWallVisibility,
  ])

  return positionVisibilityLookup
}

export default usePositionVisibilityLookup
